// Shared month grid used by the project Calendar view and My Calendar.
// Design replicates the approved My Account global calendar (gap-px gutter grid,
// Sun-first, taller cells) while keeping PMS drag-to-reschedule + "today".
// Items = {id, title, date, color, done}. Drag chip -> day cell (HTML5 DnD).
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { monthLabel, todayISO } from '../lib/dates';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function MonthGrid({ items, onItemClick, onItemDrop, draggable = false }) {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth());
  const [dragOver, setDragOver] = useState(null);

  const today = todayISO();
  const firstDay = new Date(year, month, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const iso = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const nav = (dir) => {
    let m = month + dir;
    let y = year;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  };

  const byDay = {};
  items.forEach((it) => {
    if (!it.date) return;
    (byDay[it.date] = byDay[it.date] || []).push(it);
  });

  const iconBtn =
    'rounded-md border border-pm-border p-1.5 text-pm-muted hover:border-[var(--pm-hover-border)] hover:text-pm-text';

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center justify-between">
        <button type="button" onClick={() => nav(-1)} aria-label="Previous month" className={iconBtn}>
          <ChevronLeft size={15} />
        </button>
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold tracking-tight">{monthLabel(year, month)}</h2>
          <button
            type="button"
            onClick={() => {
              setYear(now.getUTCFullYear());
              setMonth(now.getUTCMonth());
            }}
            className="rounded-md border border-pm-border px-2.5 py-1 text-xs font-medium text-pm-muted hover:border-[var(--pm-hover-border)] hover:text-pm-text"
          >
            Today
          </button>
        </div>
        <button type="button" onClick={() => nav(1)} aria-label="Next month" className={iconBtn}>
          <ChevronRight size={15} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div
          className="grid grid-cols-7 gap-px overflow-hidden rounded-lg shadow-[var(--pm-elev-1)]"
          style={{ backgroundColor: 'var(--pm-border)' }}
        >
          {WEEKDAYS.map((d, i) => (
            <div
              key={d}
              className={`bg-pm-surface py-2 text-center text-[11px] font-medium uppercase tracking-wide ${
                i === 0 || i === 6 ? 'text-pm-faint/70' : 'text-pm-faint'
              }`}
            >
              {d}
            </div>
          ))}
          {cells.map((day, i) => {
            if (!day) return <div key={`b${i}`} className="min-h-[86px] bg-transparent" />;
            const dISO = iso(day);
            const isToday = dISO === today;
            const dayItems = byDay[dISO] || [];
            return (
              <div
                key={dISO}
                onDragOver={(e) => {
                  if (!draggable) return;
                  e.preventDefault();
                  setDragOver(dISO);
                }}
                onDragLeave={() => setDragOver((d) => (d === dISO ? null : d))}
                onDrop={(e) => {
                  if (!draggable) return;
                  e.preventDefault();
                  setDragOver(null);
                  const id = e.dataTransfer.getData('text/task-id');
                  if (id) onItemDrop(id, dISO);
                }}
                className={`min-h-[86px] bg-pm-surface p-1.5 transition-colors ${
                  dragOver === dISO
                    ? 'ring-1 ring-inset ring-[var(--pm-focus-border)]'
                    : 'hover:bg-[color-mix(in_srgb,var(--pm-raised)_45%,var(--pm-surface))]'
                }`}
              >
                <span
                  className={
                    isToday
                      ? 'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-pm-accent px-1 text-[11px] font-semibold text-pm-accent-ink'
                      : 'text-xs font-medium text-pm-text'
                  }
                >
                  {day}
                </span>
                <div className="mt-1 space-y-0.5">
                  {dayItems.slice(0, 4).map((it) => (
                    <div
                      key={it.id}
                      draggable={draggable && !it.readOnly}
                      onDragStart={(e) => e.dataTransfer.setData('text/task-id', it.id)}
                      onClick={() => onItemClick(it)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && onItemClick(it)}
                      className="flex cursor-pointer items-center gap-1 truncate rounded px-1.5 py-0.5 text-[10px] leading-tight hover:brightness-110"
                      style={
                        it.done
                          ? { background: 'var(--pm-raised)', color: 'var(--pm-faint)' }
                          : { background: `${it.color}22`, color: it.color }
                      }
                      title={it.title}
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ background: it.done ? 'var(--pm-faint)' : it.color }}
                      />
                      <span className={`truncate ${it.done ? 'line-through' : ''}`}>{it.title}</span>
                    </div>
                  ))}
                  {dayItems.length > 4 && (
                    <div className="px-1 text-[9px] text-pm-faint">+{dayItems.length - 4} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
